import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useRoutes, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Alert } from '../components/alert/alert';
import { Button } from '../components/button/button';
import { FilterButton } from '../components/button/filter-button';
import { LotCard } from '../components/card/lot-card';
import { Container } from '../components/container/container';
import {
  FilterPanel,
  FilterSheet,
} from '../components/filter-sheet/filter-sheet';
import { LotsHeader } from '../components/header/lots-header';
import { Image } from '../components/image/image';
import { SearchInput } from '../components/input/search-input';
import { Timer } from '../components/timers/timer';
import { LotCardContainer } from '../containers/lot-card-container';
import {
  LotSortBy,
  LotType,
  useAllLotsQuery,
  useMeQuery,
} from '../graphql/types';
import { device } from '../styles/devices';
import { color, gutter } from '../styles/mixins';
import { p14 } from '../styles/typography';
import { trackProductListViewed } from '../utils/analytics';
import type { FilterParams } from '../utils/lot-filter-params';
import {
  getLotFilterParams,
  removeDefaultFilterParams,
  setLotFilterParams,
} from '../utils/lot-filter-params';
import useMediaQuery from '../utils/use-media-query';

// #region Styles
const MobileOnly = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`;

const Header = styled.div`
  display: grid;
  grid-template: 1fr / 102px 1fr;
  gap: 15px 17px;
  margin-bottom: 16px;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Info = styled.p`
  ${p14};
  color: ${color('gray80')};
`;

const Items = styled.div`
  flex: 1;
  margin-bottom: 32px;

  > a {
    display: block;
    margin-bottom: 16px;
  }

  > div {
    margin-bottom: 16px;
  }
`;

const Sidebar = styled.div`
  width: 240px;
  margin-right: ${gutter(6)};
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
`;
// #endregion

function searchParamsToFilter(filterParams: FilterParams) {
  const params = removeDefaultFilterParams(filterParams);
  const filter: Record<string, any> = {};

  if (params.type?.length) {
    const types: Record<string, LotType> = {
      red: LotType.Red,
      white: LotType.White,
      sparkling: LotType.Sparkling,
    };
    filter.type = params.type.map((type: string) => types[type]);
  }

  if (params.variety?.length) {
    filter.variety = params.variety;
  }

  if (params.q) {
    filter.query = params.q;
  }

  if (params.year) {
    filter.year = params.year;
  }

  return filter;
}

export function Lots() {
  const { year } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParams = getLotFilterParams(searchParams, year);
  const variables = {
    first: 25,
    filter: searchParamsToFilter(filterParams),
    sort:
      searchParams.get('sort') === 'desc'
        ? LotSortBy.LotnumberDesc
        : LotSortBy.LotnumberAsc,
  };
  const routes = useRoutes([
    {
      path: 'filters',
      element: <FilterSheet />,
    },
  ]);
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });
  const theme = useTheme();
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.breakpoints.md.width}px)`
  );
  const lots = useAllLotsQuery({
    fetchPolicy: 'cache-and-network',
    variables,
    skip: !isDesktop && !!routes,
  });

  const isLoading = lots.loading;
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [query, setQuery] = useState(filterParams.q);
  const delay = useRef<any>();
  const auction = me.data?.activeAuction;

  useEffect(() => {
    clearTimeout(delay.current);
    delay.current = setTimeout(() => {
      if (query !== filterParams.q) {
        setSearchParams(setLotFilterParams(filterParams, { q: query }), {
          replace: true,
        });
      }
    }, 300);
  }, [query, filterParams, setSearchParams]);

  useEffect(() => {
    setQuery(filterParams.q);
  }, [filterParams.q]);

  useEffect(() => {
    trackProductListViewed();
  }, []);

  const url = year ? `/${year}/lots` : '/lots';
  const startsAt = me.data?.activeAuction?.startsAt;
  const isInCurrentYear =
    new Date(startsAt ?? 0).getFullYear().toString() === year;

  return (
    <>
      <Container>
        <LotsHeader
          button={
            !!searchParams.toString().length && <Link to={url}>Clear all</Link>
          }
          search={
            <SearchInput
              type="text"
              value={query}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setQuery(e.currentTarget.value)
              }
              placeholder="Search"
            />
          }
          text={
            !!searchParams.toString().length && (
              <Info>Showing filtered result</Info>
            )
          }
        />
        <MobileOnly>
          <Header>
            <FilterButton
              as={Link}
              to={`${url}/filters?${searchParams.toString()}`}
            >
              Filters
            </FilterButton>

            <SearchInput
              type="text"
              value={query}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setQuery(e.currentTarget.value)
              }
              placeholder="Search"
            />
          </Header>
          {!!searchParams.toString().length && (
            <SubHeader>
              <Info>Showing filtered result</Info>
              <Button variant="secondary" size="sm" as={Link} to={url}>
                Clear
              </Button>
            </SubHeader>
          )}
        </MobileOnly>
        <Columns>
          {isDesktop && (
            <Sidebar>
              <FilterPanel />
            </Sidebar>
          )}
          <Items>
            {startsAt && isInCurrentYear && (
              <Timer
                value={new Date(me.data?.activeAuction?.startsAt)}
                text="Until Auction Starts"
              />
            )}
            {lots.data?.allLots?.edges?.length === 0 ? (
              <Alert copy="No lots were found with the current filters. Please adjust them and try again." />
            ) : null}
            {(lots.data?.allLots?.edges || []).map(({ node }) => (
              <LotCardContainer
                lot={node as any}
                key={node.id}
                auction={auction ?? undefined}
              />
            ))}
            {isLoading &&
              !lots.data &&
              [...Array.from({ length: variables.first })].map((_, i) => (
                <LotCard
                  key={i}
                  isLoading
                  image={<Image src="/temp/image_example_tall_2.png" alt="" />}
                  brandName="213.  Blackbird"
                  title="2019 Blackbird Arise"
                  highestBid={1200}
                  summary="2019 Blackbird"
                />
              ))}
            <Button
              variant="secondary"
              size="md"
              fullWidth
              disabled={isLoading || !lots.data?.allLots?.pageInfo.hasNextPage}
              loading={isLoadingMore}
              onClick={() => {
                setIsLoadingMore(true);
                lots
                  .fetchMore({
                    variables: {
                      ...variables,
                      after: lots.data?.allLots?.pageInfo.endCursor,
                    },
                  })
                  .then(() => {
                    setIsLoadingMore(false);
                  });
              }}
            >
              {!lots.data?.allLots?.pageInfo.hasNextPage
                ? 'You have reached the end'
                : 'Load more…'}
            </Button>
          </Items>
        </Columns>
      </Container>
      <AnimatePresence exitBeforeEnter>{routes}</AnimatePresence>
    </>
  );
}
